import { useCallback, useMemo, useRef, useState } from "react";
import {
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
} from "../firebase";
import { isDefaultTemplateAutomations, uniqueVals } from "@markit/common.utils";
import { Campaign, CampaignType } from "@markit/common.types";
import { getUserCampaignsRef } from "../utils/FirebaseUtils";
import { useSelector, useDispatch, batch } from "react-redux";
import {
  campaignActions,
  CampaignDetails,
  getCampaignState,
} from "../redux/slices/campaignSlice";
import useOnUnmount from "./useOnUnmount";
import { useOnMount } from "./useOnMount";
import { fetchCampaignDetails } from "../utils/campaignUtils";

type useLoadAutomationItemsProps = {
  userId: string;
  windowSize: number;
};

export const useLoadAutomationItems = (props: useLoadAutomationItemsProps) => {
  const { userId, windowSize } = props;
  const dispatch = useDispatch();
  const { campaigns, campaignDetails } = useSelector(getCampaignState);
  const [fetchedAutomations, setFetchedAutomations] = useState<Campaign[]>([]);
  const [fetchedAutomationDetails, setFetchedAutomationDetails] = useState<
    CampaignDetails[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedAllAutomations, setLoadedAllAutomations] = useState(false);
  const [lastAutomationVisibleTimestamp, setLastAutomationVisibleTimestamp] =
    useState("");
  const unsubscribeArrayRef = useRef<(() => void)[]>([]);

  const filteredAutomations = useMemo(
    () =>
      campaigns.filter((campaign) => campaign.type === CampaignType.AUTOMATION),
    [campaigns]
  );

  useOnMount(() => {
    setFetchedAutomations(
      filteredAutomations.filter(
        (campaign) => !isDefaultTemplateAutomations(campaign.id)
      )
    );
    setFetchedAutomationDetails(
      campaignDetails.filter(
        (detail) => !isDefaultTemplateAutomations(detail.campaignId)
      )
    );
  });

  useOnUnmount(
    (latestDeps) => {
      batch(() => {
        dispatch(
          campaignActions.addMultipleCampaigns(latestDeps[0] as Campaign[])
        );
        dispatch(
          campaignActions.addMultipleCampaignDetails(
            latestDeps[1] as CampaignDetails[]
          )
        );
      });
      unsubscribeArrayRef.current.forEach(
        (unsubscribe) => unsubscribe && unsubscribe()
      );
      unsubscribeArrayRef.current = []; // Clear the array after cleanup
    },
    [fetchedAutomations, fetchedAutomationDetails]
  );

  const addCampaignData = useCallback((newCampaignData: Campaign[]) => {
    const automations = newCampaignData.filter(
      (campaign) => !isDefaultTemplateAutomations(campaign.id)
    );
    if (automations.length === 0) {
      return;
    }
    setFetchedAutomations((fetchedAutomations) =>
      uniqueVals(
        automations.concat(fetchedAutomations),
        (campaign) => campaign.id
      ).sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
    );
  }, []);

  const removeCampaignData = useCallback((campaign: Campaign) => {
    setFetchedAutomations((fetchedAutomations) => {
      const index = fetchedAutomations.findIndex(
        (fetched) => fetched.id === campaign.id
      );
      if (index !== -1) {
        fetchedAutomations.splice(index, 1);
      }
      return [...fetchedAutomations];
    });
  }, []);

  const addCampaignDetailsData = useCallback(
    (newCampaignDetails: CampaignDetails[]) => {
      setFetchedAutomationDetails((fetchedAutomationDetails) =>
        uniqueVals(
          newCampaignDetails.concat(fetchedAutomationDetails),
          (detail) => detail.campaignId
        )
      );
    },
    []
  );

  const removeCampaignDetailsData = useCallback((campaignId: string) => {
    setFetchedAutomationDetails((fetchedAutomationDetails) => {
      const index = fetchedAutomationDetails.findIndex(
        (fetched) => fetched.campaignId === campaignId
      );
      if (index !== -1) {
        fetchedAutomationDetails.splice(index, 1);
      }
      return [...fetchedAutomationDetails];
    });
  }, []);

  const isFinished = useMemo(
    () => loadedAllAutomations,
    [loadedAllAutomations]
  );

  const updateData = useCallback(
    async (foundCampaignData: Campaign[]) => {
      addCampaignData(foundCampaignData);
      const campaignDetails = await Promise.all(
        foundCampaignData.map(async (campaign) => {
          return await fetchCampaignDetails(userId, campaign);
        })
      );
      addCampaignDetailsData(campaignDetails);

      batch(() => {
        dispatch(campaignActions.addMultipleCampaigns(foundCampaignData));
        dispatch(campaignActions.addMultipleCampaignDetails(campaignDetails));
      });
    },
    [addCampaignData, addCampaignDetailsData, dispatch, userId]
  );

  const initialUpdateData = useCallback(
    async (
      visibleCampaigns: Campaign[],
      batchSize: number,
      lastUpdatedAt?: string
    ) => {
      const newCampaigns = visibleCampaigns.filter(
        (loadedCampaign) =>
          !filteredAutomations.some(
            (campaign) =>
              campaign.id === loadedCampaign.id &&
              campaign.updatedAt === loadedCampaign.updatedAt
          )
      );

      if (visibleCampaigns.length > 0) {
        await updateData(newCampaigns);
      }
      setIsLoading(false);

      if (batchSize > 0 && lastUpdatedAt) {
        setLastAutomationVisibleTimestamp(lastUpdatedAt);
      }
      if (batchSize < windowSize) {
        setLoadedAllAutomations(true);
      }
    },
    [filteredAutomations, updateData, windowSize]
  );

  const liveUpdateCampaigns = useCallback(
    async (
      type: "added" | "modified" | "removed",
      campaign: Campaign,
      foundCampaigns: Campaign[]
    ) => {
      if (
        (type === "added" &&
          !foundCampaigns.some(
            (campaignData) => campaignData.id === campaign.id
          )) ||
        type === "modified"
      ) {
        updateData([campaign]);
      } else if (type === "removed") {
        removeCampaignData(campaign);
        removeCampaignDetailsData(campaign.id);
      }
    },
    [removeCampaignData, removeCampaignDetailsData, updateData]
  );

  const loadAutomations = useCallback(async () => {
    setIsLoading(true);
    const campaignsRef = getUserCampaignsRef(userId);
    const campaignsQuery = query(
      campaignsRef,
      where("type", "==", CampaignType.AUTOMATION),
      orderBy("updatedAt", "desc"),
      limit(windowSize)
    );

    const allCampaigns = (await getDocs(campaignsQuery)).docs.map((doc) =>
      doc.data()
    );
    const visibleCampaigns = allCampaigns.filter(
      (campaign) => !isDefaultTemplateAutomations(campaign.id)
    );
    const lastUpdatedAt =
      allCampaigns.length > 0
        ? allCampaigns[allCampaigns.length - 1].updatedAt
        : undefined;
    await initialUpdateData(visibleCampaigns, allCampaigns.length, lastUpdatedAt);

    const unsubscribe = onSnapshot(campaignsQuery, (snapshot) => {
      snapshot.docChanges().forEach(async (message) => {
        const campaign = message.doc.data();
        if (isDefaultTemplateAutomations(campaign.id)) {
          return;
        }
        await liveUpdateCampaigns(message.type, campaign, allCampaigns);
      });
    });
    unsubscribeArrayRef.current.push(unsubscribe);
  }, [initialUpdateData, liveUpdateCampaigns, userId, windowSize]);

  const loadMoreAutomations = useCallback(async () => {
    if (!loadedAllAutomations && !isLoading) {
      setIsLoading(true);
      const campaignsRef = getUserCampaignsRef(userId);
      const campaignsQuery = query(
        campaignsRef,
        where("type", "==", CampaignType.AUTOMATION),
        orderBy("updatedAt", "desc"),
        startAfter(lastAutomationVisibleTimestamp),
        limit(windowSize)
      );

      const allCampaigns = (await getDocs(campaignsQuery)).docs.map((doc) =>
        doc.data()
      );
      const visibleCampaigns = allCampaigns.filter(
        (campaign) => !isDefaultTemplateAutomations(campaign.id)
      );
      const lastUpdatedAt =
        allCampaigns.length > 0
          ? allCampaigns[allCampaigns.length - 1].updatedAt
          : undefined;
      await initialUpdateData(visibleCampaigns, allCampaigns.length, lastUpdatedAt);

      const unsubscribe = onSnapshot(campaignsQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (message) => {
          const campaign = message.doc.data();
          if (isDefaultTemplateAutomations(campaign.id)) {
            return;
          }
          await liveUpdateCampaigns(message.type, campaign, allCampaigns);
        });
      });
      unsubscribeArrayRef.current.push(unsubscribe);
    }
  }, [
    initialUpdateData,
    isLoading,
    lastAutomationVisibleTimestamp,
    liveUpdateCampaigns,
    loadedAllAutomations,
    userId,
    windowSize,
  ]);

  return {
    fetchedAutomations,
    fetchedAutomationDetails,
    isLoading,
    isFinished,
    loadAutomations,
    loadMoreAutomations,
  };
};
